/** Every user-facing string lives here, keyed, so a translation is data and not a code change.
 * English only for now; Amharic is parked (decision log). */
const en = {
  appName: 'ASSIST',
  loading: 'Opening ASSIST…',
  signInTitle: 'Sign in',
  signInHelp:
    'Use your ASSIST username and password. You need the internet the first time on this device.',
  username: 'Username',
  password: 'Password',
  signIn: 'Sign in',
  signingIn: 'Signing in…',
  showPassword: 'Show',
  hidePassword: 'Hide',
  pinTitle: 'Choose your PIN',
  pinHelp:
    'You will use this PIN to open ASSIST on this device, also without internet. 6 to 12 digits.',
  pin: 'PIN',
  pinAgain: 'PIN again',
  pinMismatch: 'The two PINs are not the same',
  savePin: 'Save PIN and continue',
  unlockTitle: 'Who is using this device?',
  unlock: 'Open',
  otherUser: 'Sign in with a password',
  lock: 'Lock',
  syncNow: 'Sync now',
  synced: 'Synced',
  syncing: 'Syncing…',
  offline: 'Offline',
  pendingN: (n: number) => `${n} to send`,
  failedN: (n: number) => `${n} failed`,
  lastSync: (when: string) => `Last sync ${when}`,
  never: 'never',
  patients: 'Patients',
  searchPatients: 'Search by name, MRN or phone',
  registerPatient: 'Register patient',
  noPatients: 'No patients yet on this device.',
  noMatches: 'No patient matches that search.',
  firstName: 'First name',
  fatherName: "Father's name",
  grandfatherName: "Grandfather's name",
  sex: 'Sex',
  female: 'Female',
  male: 'Male',
  otherSex: 'Other',
  sexNotRecorded: 'Not recorded',
  birthDate: 'Date of birth (E.C.)',
  ageInstead: 'Not known: enter age',
  dateInstead: 'Enter date of birth',
  ageYears: 'Age in years',
  ageLabel: 'Age',
  motherLabel: 'Mother',
  estimated: 'estimated',
  phone: 'Phone',
  mrn: 'MRN (card number)',
  kebele: 'Kebele',
  woreda: 'Woreda',
  save: 'Save',
  saving: 'Saving…',
  cancel: 'Cancel',
  searchChoices: 'Type a few letters of the name or the code',
  noMatch: 'Nothing matches. Check the spelling, or write it in the notes.',
  removeChoice: (name: string) => `Remove ${name}`,
  edit: 'Edit',
  back: 'Back',
  required: 'Required',
  badPhone: 'Use 09…, 07… or +251… (10 digits)',
  badAge: 'Age must be 0 to 120',
  possibleDuplicate: 'A patient with this MRN is already on this device:',
  saveAnyway: 'Save anyway',
  savedLocally: 'Saved on this device. It will be sent when the connection allows.',
  notOnServer: 'Not yet on the server',
  /** A baby's age in days, then months, until two years; then years. */
  age: (a: { years: number; months?: number; days?: number; estimated: boolean }) =>
    !a.estimated && a.days !== undefined && a.days >= 0 && a.days < 31
      ? `${a.days} ${a.days === 1 ? 'day' : 'days'}`
      : !a.estimated && a.months !== undefined && a.months < 24
        ? `${a.months} ${a.months === 1 ? 'month' : 'months'}`
        : `${a.years} yrs${a.estimated ? ' (est.)' : ''}`,
  programmes: 'Programmes',
  noProgrammes: 'Not enrolled in any programme.',
  since: (d: string) => `since ${d}`,
  newVisit: (title: string) => `New visit: ${title}`,
  noPacks:
    'No care programmes are switched on for this facility yet. An administrator can switch them on under Settings.',
  visits: 'Visits',
  noVisits: 'No visits recorded yet.',
  nextVisit: 'Next visit',
  medicines: 'Current medicines',
  noMedicines: 'No medicines recorded.',
  yes: 'Yes',
  no: 'No',
  notAsked: 'Not asked',
  calculated: 'calculated',
  decisionSupport: 'Decision support',
  draftBanner:
    'PILOT: these rules are drafts that have not been clinically approved. Use clinical judgement and the national protocol; report anything that looks wrong.',
  noRulesInPack:
    'This form has no decision support: it records what you write. Advice for particular diseases comes from their own programmes.',
  rulesAwaitApproval:
    'The rules for this programme are awaiting clinical approval, so no advice is shown. Record the visit as usual.',
  nothingYet: 'Enter findings to see advice.',
  draft: 'DRAFT',
  why: 'Why?',
  source: 'Source',
  rule: 'Rule',
  apply: 'Apply',
  applied: 'Applied to the medicine list below; check it before saving.',
  enrolIn: (p: string) => `Enrol in ${p}`,
  enrolling: (p: string) => `Will be enrolled in ${p} when you save`,
  alreadyDiagnosed: 'Already diagnosed elsewhere?',
  groupUrgent: 'Act now',
  groupRefer: 'Referral',
  groupTreatment: 'Treatment',
  groupTests: 'Tests',
  groupAdvice: 'Advice and checks',
  groupFindings: 'Findings',
  medicine: 'Medicine',
  perDay: 'per day',
  timesPerDay: 'times a day',
  dosing: (n: number) => (n === 1 ? 'once daily' : n === 2 ? 'twice daily' : `${n} times daily`),
  current: 'now',
  addMedicine: 'Add a medicine…',
  stopMedicine: 'Stop',
  changed: 'changed',
  returnIn: 'Return in (days)',
  returnOn: (d: string) => `on ${d}`,
  suggestedDays: (n: number) => `Suggested: ${n} days`,
  saveVisit: 'Save visit',
  saveAssessment: 'Save assessment',
  adviceN: (n: number) => `Advice (${n})`,
  fixErrors: 'Correct the highlighted values first.',
  emptyVisit: 'Nothing recorded yet.',
  visitSaved: 'Visit saved on this device.',
  settings: 'Settings',
  packsTitle: 'Care programmes for this facility',
  packsHelp:
    'Switch a programme on to show its visit form. "Pilot draft rules" shows decision support that has not yet been clinically approved, flagged as DRAFT on every screen; use it only in a supervised pilot.',
  enabled: 'Switched on',
  pilotDrafts: 'Pilot draft rules',
  rulesCount: (approved: number, total: number) =>
    `${approved} of ${total} rules clinically approved`,
  needsOnline: 'Changing settings needs the internet.',
  smsConsent: 'Agrees to SMS reminders of visits',
  navPatients: 'Patients',
  navFollowup: 'Follow-up',
  navNumbers: 'Clinic numbers',
  followupTitle: 'Follow-up',
  tabOverdue: 'Overdue',
  tabDue: 'Due today',
  tabPromised: 'Promised to come',
  tabLost: 'Lost to follow-up',
  tabReferrals: 'Open referrals',
  emptyList: 'Nobody here.',
  daysLate: (n: number) => (n === 1 ? '1 day late' : `${n} days late`),
  dueOn: (d: string) => `Due ${d}`,
  comesOn: (d: string) => `Said they will come by ${d}`,
  noVisitSince: (d: string) => `No visit since ${d}`,
  attemptsN: (n: number) => (n === 1 ? '1 tracing attempt' : `${n} tracing attempts`),
  followupCard: 'Follow-up',
  nextDue: (d: string) => `Next visit due ${d}`,
  defaultInterval: '(no date set at the last visit; default interval)',
  recordAttempt: 'Record a call or home visit',
  method: 'How',
  methodPhone: 'Phone call',
  methodHome: 'Home visit',
  methodCommunity: 'Health extension worker',
  outcome: 'Result',
  outcomes: {
    will_return: 'Will come back',
    no_answer: 'No answer',
    unreachable: 'Phone off / not at home',
    wrong_number: 'Wrong number',
    refused: 'Does not want to come back',
    transferred: 'Moved or transferred to another facility',
    died: 'Died',
  } as Record<string, string>,
  returnInDays: 'Will come in (days)',
  note: 'Note',
  saveAttempt: 'Save',
  diedWarning: 'This marks the patient as deceased and ends their programme enrolment.',
  transferWarning: 'This ends the patient’s programme enrolment at this facility.',
  attemptsTitle: 'Tracing attempts since the last visit',
  referrals: 'Referrals',
  noReferrals: 'No referrals.',
  referralOpen: 'Open',
  referralOutcomes: {
    completed: 'Seen at the referral site',
    not_attended: 'Did not go',
    died: 'Died on the way or there — record the death on her record',
    cancelled: 'Cancelled',
  } as Record<string, string>,
  recordOutcome: 'Record outcome',
  urgent: 'Urgent',
  routine: 'Routine',
  emergency: 'Emergency',
  urgencies: { urgent: 'Urgent', emergency: 'Emergency', routine: 'Routine' } as Record<
    string,
    string
  >,
  transport: 'Transport',
  referralDetails: 'Details',
  recordReferral: 'Record a referral',
  otherReferral: 'Other referral (reason)',
  referTo: 'Refer to (facility or service)',
  numbersTitle: 'Clinic numbers',
  numbersHelp: (d: string) => `Calculated on this device from the records it holds, as of ${d}`,
  ofN: (n: number, d: number) => `${n} of ${d}`,
  computing: 'Counting…',
  deceased: 'Deceased',
  riskWithheld: 'Not enough of the core measurements yet to estimate risk.',
  raisedBy: 'Raised most by',
  raisedBySafety: 'Raised by danger signs',
  measuredOf: (n: number, total: number) => `Based on ${n} of ${total} inputs measured.`,
  riskAdvisory: 'An aid to your judgement; the guideline alerts above and below always apply.',
  bandLabel: { green: 'Low', amber: 'Raised', red: 'High' } as Record<string, string>,
  hmisTitle: 'Monthly HMIS report',
  hmisHelp: (from: string, to: string) =>
    `Reporting month ${from} to ${to} (21st to 20th, as the national HMIS). Calculated on this device.`,
  hmisOpen: 'Monthly HMIS report',
  year: 'Year',
  month: 'Month',
  element: 'Indicator',
  value: 'Value',
  outsideBands: (n: number) =>
    `${n} counted in the total but outside the age bands (check birth dates)`,
  sendDhis2: 'Send to DHIS2',
  sending: 'Sending…',
  dhis2DryRun: 'DRY RUN: DHIS2 checked the data and stored nothing.',
  dhis2Sent: (i: number, u: number, ig: number) =>
    `DHIS2: ${i} imported, ${u} updated, ${ig} ignored.`,
  dhis2Unmapped: (n: number) => `${n} values have no DHIS2 mapping and were not sent.`,
  dhis2ServerNote: 'The server sends what has been synced to it; sync this device first.',
  facilityIds: 'Facility identifiers',
  dhis2OrgUnit: 'DHIS2 org unit id',
  mfrId: 'Master Facility Registry id',
  dhis2Configured: (dry: boolean) =>
    `DHIS2 reporting is set up on the server${dry ? ' (dry run only)' : ''}.`,
  dhis2NotConfigured: 'DHIS2 reporting is not set up on the server yet.',
  saved: 'Saved.',
  groupLabels: {
    'sex,age': 'By sex and age',
    care: 'By type of care',
    timing: 'By enrolment',
    outcome: 'By outcome',
  } as Record<string, string>,
  keyLabels: {
    hlc: 'Lifestyle counselling only',
    pharm: 'Medicines and counselling',
    new: 'Newly enrolled',
    previous: 'Previously in care',
    controlled: 'Controlled',
    uncontrolled: 'Uncontrolled',
    ltfu: 'Lost to follow-up',
    died: 'Died',
    transferred_out: 'Transferred out',
    not_evaluated: 'Not evaluated',
    male: 'Male',
    female: 'Female',
    total: 'Total',
    registered: 'Cohort registered',
  } as Record<string, string>,
  failedTitle: 'Records the server refused',
  failedHelp: 'These stay on this device. Show them to your supervisor.',
  revokedTitle: 'This device was revoked',
  revokedHelp:
    'An administrator revoked this device, so its local data has been erased. Ask your administrator before using it again.',
  sessionEnded: 'Your session ended. Sign in with your password to sync again; your work is kept.',
  updateReady: 'A new version of ASSIST is ready.',
  reload: 'Reload',
  clinicTime: 'Addis Ababa',
  version: (v: string) => `ASSIST v${v}`,
  fullRecord: 'Full record',
  requestTest: 'Request this test from the laboratory',
  ackDangerTitle: 'Read these before saving:',
  ackDangerSave: 'Press Save again to record this visit.',
  labOrders: 'Laboratory requests',
  labResult: 'Result',
  recordResult: 'Record the result',
  pending: 'pending',
  herBabies: 'Her babies',
  noBabies: 'No newborn registered yet.',
  born: 'born',
  registerNewborn: 'Register a newborn',
  registerNewbornHelp:
    'The baby gets a record of her or his own, linked to the mother. Register each twin separately. Weight, Apgar and the newborn checks are recorded on the baby’s record.',
  babyOf: (m: string) => `Baby of ${m}`,
  babyName: 'Name (optional)',
  bornAt: 'Born at (date and time)',
  chooseSex: 'Choose the baby’s sex.',
  birthTimeInvalid: 'Enter a birth time that is not in the future.',
  otherServices: 'Other services',
  print: 'Print',
  printedOn: (d: string) => `Printed ${d}`,
  nothingRecorded: 'Nothing recorded.',
  supervisorTitle: 'Supervision board',
  supervisorHelp:
    'Every facility in your area, from their records on the server (needs a connection).',
  facilityCol: 'Facility',
  referralLetter: 'Referral letter',
  printLetter: 'Referral letter',
  forNewborn: 'THIS REFERRAL IS FOR THE NEWBORN.',
  forNewbornHelp:
    'The baby is the patient. The mother’s details are below because the receiving unit will need them — her HIV status, her blood group, how the baby was born.',
  motherCol: 'Mother',
  whyReferred: 'Why the referral',
  urgencyLabel: 'Urgency',
  dateCol: 'Date',
  reasonLabel: 'Reason',
  noReferral:
    'No referral has been recorded. Record it at a visit and this letter will carry the reason.',
  foundAtVisit: 'What we found at that visit',
  outstanding: 'Still outstanding at the time of referral',
  referringProvider: 'Referring provider',
  signatureLine: 'Signature: ______________________________    Phone: ______________________',
  registersTitle: 'MoH registers',
  registersHelp:
    'The official Ministry of Health registers, generated from what has been recorded. Person-level items (target population, HIV linkage, partner testing) fill themselves on every row. Print, or export to CSV.',
  registerLabel: 'Register',
  monthLabel: 'Month',
  yearLabel: 'Year',
  exportCsv: 'Export CSV',
  rowsN: (n: number) => `${n} row${n === 1 ? '' : 's'}`,
  nothingInPeriod: 'Nothing recorded in this period.',
  subRegisters: 'Print · CSV',
  overviewTitle: 'Facility overview',
  overviewHelp: 'Calculated on this device from every record it holds.',
  periodLabel: 'Period',
  periods: {
    0: 'All time',
    30: 'Last 30 days',
    90: 'Last 90 days',
    365: 'Last 12 months',
  } as Record<number, string>,
  trendsTitle: 'The last six months',
  vsBaseline: 'change vs baseline',
  // ---- episodes and time series (the Labour Care Guide)
  assessmentsN: (n: number) => `${n} assessment${n === 1 ? '' : 's'}`,
  latestAlerts: (n: number) => `The latest assessment: ${n} finding${n === 1 ? '' : 's'} to act on`,
  observation: 'Observation',
  alertCol: 'Alert',
  latent: 'latent',
  sinceCol: 'Since',
  lastAssessmentCol: 'Last assessment',
  staleEntry: (hours: number) =>
    hours >= 48
      ? `Nothing recorded for ${Math.floor(hours / 24)} days: is she still here? Close the episode if she has left.`
      : `Nothing recorded for ${hours} hours: is she still here? Close the episode if she has left.`,
  staleCount: (n: number) => `${n} with nothing recorded for over a day`,
  closeEpisode: 'Close this episode of care',
  closeEpisodeHelp: 'Takes her off the ward list. The record stays, and can be read.',
  closeEpisodeSave: 'Close the episode',
  removeRecord: 'Remove this record',
  removeRecordHelp:
    'Only for a record entered in error: registered twice, or for the wrong person. Nothing is deleted: the record leaves every list, register and report, and an admin can restore it.',
  removeReasonHint: 'e.g. registered twice — the right record is MRN 1234',
  removeRecordSave: 'Remove the record',
  removedTitle: 'Removed record.',
  removedBy: (by: string, on: string) => `Removed by ${by || '—'} on ${on}.`,
  removedReason: 'Why',
  restore: 'Restore',
  removedListTitle: 'Removed records',
  removedListHelp:
    'Records removed as entered in error. They are kept, readable, and count nowhere. Restore one to put it back exactly as it was.',
  removedNone: 'Nothing has been removed.',
  removedEpisodeOf: (name: string) => `Episode of care of ${name}`,
  removedPill: 'removed',
  endReasons: {
    completed: 'Completed (she has given birth)',
    referred: 'Referred to another facility',
    'transferred-out': 'Transferred out',
    died: 'Died',
    'entered-in-error': 'Opened in error',
  } as Record<string, string>,
  // ---- the frame (side navigation, bottom tabs, home launcher), as in ASSIST-MNH
  tagline: 'Primary health care',
  footer: 'ASSIST — Decision support for primary health care',
  mainNav: 'Main',
  stepsNav: 'Steps of care',
  grpCare: 'Care',
  grpProgrammes: 'Programmes',
  grpReports: 'Reports',
  grpAdmin: 'Admin',
  grpYou: 'You',
  navHome: 'Home',
  navFind: 'Find a patient',
  navMe: 'My account',
  shHome: 'Home',
  shRegister: 'Register',
  shFind: 'Find',
  shFollowup: 'Follow-up',
  shNumbers: 'Numbers',
  shHmis: 'HMIS',
  shRegisters: 'Registers',
  shBoard: 'Board',
  shReports: 'Reports',
  shRefused: 'Refused',
  shMe: 'Me',
  roleName: {
    admin: 'admin',
    provider: 'provider',
    recorder: 'recorder',
    supervisor: 'supervisor',
    observer: 'observer',
  } as Record<string, string>,
  welcome: (name: string) => `Welcome, ${name}`,
  needsAttention: 'Needs attention',
  subRegister: 'New patient',
  subFind: 'Already registered',
  subFollowup: (overdue: number, due: number) =>
    overdue || due ? `${overdue} overdue · ${due} due today` : 'Nobody waiting',
  inCare: (n: number) => `${n} in care`,
  subNumbers: 'Indicators · cohort outcomes',
  subHmis: 'eHMIS · DHIS2',
  subSettings: 'Programmes · facility IDs',
  subMe: 'Profile · this device',
  hub: 'Hub',
  stepSummary: 'Summary',
  stepDetails: 'Personal details',
  editDetails: 'Personal details',
  upcoming: 'Upcoming',
  noneInCare: 'Nobody is in care in this programme on this device yet.',
  nameCol: 'Name',
  lastVisitCol: 'Last visit',
  dueCol: 'Due',
  statusCol: 'Status',
  fullName: 'Name',
  roleLabel: 'Role',
  facility: 'Facility',
  toSend: 'Waiting to send',
  lastSyncLabel: 'Last sync',
  versionLabel: 'Version',
  lockDevice: 'Lock this device',
  lockHelp:
    'Locking keeps everything on this device, encrypted. Open it again with your PIN; a colleague opens it with theirs.',
  loginHeadline: 'Decision support for primary health care',
  loginSub:
    'Guideline rules and on-device prediction models, for the health centre and the health post.',
  loginPoints: [
    [
      'sync',
      'Works offline.',
      'Records stay on the tablet, encrypted, and sync when there is a connection.',
    ],
    [
      'ai',
      'Advice with its reasons.',
      'Every alert shows the guideline and the findings behind it.',
    ],
    [
      'dashboard',
      'Follow-up and reports.',
      'Who to trace today, and the monthly HMIS report from the same records.',
    ],
  ] as ReadonlyArray<readonly [string, string, string]>,
  loginFoot: 'Ethiopia · primary health care',
} as const;

export type Strings = typeof en;
export const t: Strings = en;
